import React from "react";
import { cn } from "@/utils/cn";

interface ActionButtonProps {
  onClick: (e: React.MouseEvent) => void;
  title: string;
  icon: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  variant?: "default" | "danger";
  className?: string;
}

export const ActionButton: React.FC<ActionButtonProps> = ({
  onClick,
  title,
  icon,
  loading = false,
  disabled = false,
  variant = "default",
  className,
}) => (
  <button
    onClick={(e) => {
      e.stopPropagation();
      if (!disabled && !loading) {
        onClick(e);
      }
    }}
    disabled={disabled || loading}
    className={cn(
      "p-1.5 rounded-full shadow-xs transition-all hover:shadow-md",
      variant === "default" && "bg-white/90 hover:bg-white text-gray-700",
      variant === "danger" && "bg-red-500/90 hover:bg-red-600 text-white",
      (disabled || loading) && "opacity-50 cursor-not-allowed",
      className
    )}
    title={title}
    data-interactive="true"
  >
    {loading ? (
      <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
    ) : (
      icon
    )}
  </button>
);

interface DownloadButtonProps {
  onClick: (e: React.MouseEvent) => void;
  loading?: boolean;
}

export const DownloadButton: React.FC<DownloadButtonProps> = ({
  onClick,
  loading = false,
}) => (
  <ActionButton
    onClick={onClick}
    loading={loading}
    title="Download original file"
    icon={
      <svg
        className="w-4 h-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
        />
      </svg>
    }
  />
);

interface DeleteButtonProps {
  onClick: (e: React.MouseEvent) => void;
  loading?: boolean;
}

export const DeleteButton: React.FC<DeleteButtonProps> = ({
  onClick,
  loading = false,
}) => (
  <ActionButton
    onClick={onClick}
    loading={loading}
    title="Delete"
    variant="danger"
    icon={
      <svg
        className="w-4 h-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
        />
      </svg>
    }
  />
);

interface EditButtonProps {
  onClick: (e: React.MouseEvent) => void;
  loading?: boolean;
}

export const EditButton: React.FC<EditButtonProps> = ({
  onClick,
  loading = false,
}) => (
  <button
    onClick={(e) => {
      e.stopPropagation();
      if (!loading) {
        onClick(e);
      }
    }}
    disabled={loading}
    className={cn(
      "w-6 h-6 bg-blue-500/90 hover:bg-blue-600/90 text-white rounded-full flex items-center justify-center shadow-xs cursor-pointer transition-colors",
      loading && "opacity-50 cursor-not-allowed"
    )}
    title="Edit for Instagram formats"
    data-interactive="true"
  >
    {loading ? (
      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
    ) : (
      <svg
        className="w-3.5 h-3.5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
        />
      </svg>
    )}
  </button>
);

interface ReprocessButtonProps {
  onClick: (e: React.MouseEvent) => void;
  loading?: boolean;
  title?: string;
  variant?: "default" | "danger";
}

export const ReprocessButton: React.FC<ReprocessButtonProps> = ({
  onClick,
  loading = false,
  title = "Reprocess",
  variant = "default",
}) => (
  <ActionButton
    onClick={onClick}
    loading={loading}
    title={title}
    variant={variant}
    icon={
      <svg
        className="w-4 h-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </svg>
    }
  />
);

interface AiAnalysisButtonProps {
  onClick: (e: React.MouseEvent) => void;
  loading?: boolean;
  title?: string;
}

export const AiAnalysisButton: React.FC<AiAnalysisButtonProps> = ({
  onClick,
  loading = false,
  title = "AI Analysis",
}) => (
  <ActionButton
    onClick={onClick}
    loading={loading}
    title={title}
    icon={
      <svg
        className="w-4 h-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
        />
      </svg>
    }
  />
);

interface RotateButtonProps {
  onClick: (e: React.MouseEvent) => void;
  loading?: boolean;
  direction: "clockwise" | "counterclockwise";
}

export const RotateButton: React.FC<RotateButtonProps> = ({
  onClick,
  loading = false,
  direction,
}) => (
  <ActionButton
    onClick={onClick}
    loading={loading}
    title={direction === "clockwise" ? "Rotate clockwise" : "Rotate counterclockwise"}
    icon={
      <svg
        className="w-4 h-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        style={{
          transform: direction === "counterclockwise" ? "scaleX(-1)" : undefined,
        }}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </svg>
    }
  />
);
